import React, { createContext, useContext, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import useCurrencies from './useCurrencies';
import usePeriods from './usePeriods';
import useSizes from './useSizes';
import useGetQuote from './useGetQuote';
import { useDataroomTenantContext } from '@/dataroom/application/DataroomTenantContext';
import config from '@/dataroom/application/config/config';

const useQuote = () => {
  const { tenant } = useDataroomTenantContext();

  const {
    isFetching: isCurrenciesFetching,
    collection: currenciesCollection,
    defaultCurrencyId,
    getCurrencies,
  } = useCurrencies();

  const {
    isFetching: isPeriodsFetching,
    collection: periodsCollection,
    getPeriods,
  } = usePeriods();

  const {
    isFetching: isSizesFetching,
    collection: sizesCollection,
    getSizes,
  } = useSizes();

  const {
    isFetching: isGetQuoteFetching,
    submittedFormValues,
    getQuote,
    resetGetQuote,
  } = useGetQuote();

  useEffect(() => {
    if (tenant === config.tenant.tenantDealvdr.code) {
      getCurrencies();
      getPeriods();
      getSizes();
    }
  }, []);

  const isFetching = tenant === config.tenant.tenantDealvdr.code
    ? isCurrenciesFetching || isPeriodsFetching || isSizesFetching || isGetQuoteFetching
    : isGetQuoteFetching;

  return {
    isFetching,
    currenciesCollection,
    periodsCollection,
    sizesCollection,
    defaultCurrencyId,
    defaultPeriodId: periodsCollection[0]?.value,
    defaultSizeId: sizesCollection[0]?.value,
    isSuccessScreen: !isEmpty(submittedFormValues),
    submittedFormValues,
    getQuote,
    resetGetQuote,
  };
};

type QuoteContextType = ReturnType<typeof useQuote>;

export const QuoteContext = createContext<QuoteContextType>(null);

export const useQuoteContext = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuoteContext must be used within a QuoteContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const QuoteContextProvider = ({ children }: IProps) => (
  <QuoteContext.Provider value={ useQuote() }>
    { children }
  </QuoteContext.Provider>
);

export default QuoteContextProvider;
